/**
 * Map popup placement. A listing popup opens beside its marker WITHOUT moving
 * the map: try the caller's preferred side, then above, below, right and left
 * of the anchor, slide along the anchor edge to stay inside the container
 * margins and clear of the map's own controls, and fall back to the roomiest
 * side (clamped, `clipped`) when nothing fits. Once the anchor leaves the
 * container the popup moves rigidly with it instead (`followPopup`). Pure pixel
 * geometry in map-container coordinates, so it is testable without a map and
 * the caller owns the DOM.
 */

/** Top-left corner and size, in pixels relative to the map container. */
export interface PixelBox { x: number; y: number; width: number; height: number }
export interface PixelSize { width: number; height: number }
export type PopupSide = 'above' | 'below' | 'right' | 'left'
/** `x`/`y` is the popup's top-left; `arrow` is the arrow centre's offset along the popup edge facing the anchor. */
export interface PopupPlacement { side: PopupSide; x: number; y: number; arrow: number; clipped: boolean }
/**
 * `gap`: anchor edge to popup edge (the arrow lives in it); `margin`: minimum distance to the container edges
 * and to `avoid` boxes; `arrowInset`: minimum popup corner to arrow centre; `prefer`: side tried first when it
 * fits (the fallback ignores it); `avoid`: boxes drawn above the popup (map controls) that it must not cover.
 */
export interface PlacementOptions { gap?: number; margin?: number; arrowInset?: number; prefer?: PopupSide; avoid?: readonly PixelBox[] }
/** A placement and the popup's offset from its anchor's top-left. */
export interface PopupFollow { placement: PopupPlacement; dx: number; dy: number }
/** `inside`: the anchor overlaps the container, so this placement may serve as the next `last`. */
export interface FollowedPlacement extends PopupFollow { inside: boolean }

const SIDES: readonly PopupSide[] = ['above', 'below', 'right', 'left']

/** Clamp into [lo, hi]; an empty range (popup larger than the room) aligns to `lo`, the top/left margin. */
function clamp(value: number, lo: number, hi: number): number {
  return hi < lo ? lo : Math.min(Math.max(value, lo), hi)
}

/** Arrow offset along an edge of `length`; an edge too short for both insets centres the arrow. */
function arrowOffset(offset: number, length: number, inset: number): number {
  return length - inset < inset ? length / 2 : clamp(offset, inset, length - inset)
}

const intersects = (a: PixelBox, b: PixelBox): boolean =>
  a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height

const vertical = (side: PopupSide): boolean => side === 'above' || side === 'below'
/** An unknown `prefer` (odd input from untyped callers) counts as none. */
const preferred = (prefer: PopupSide | undefined): PopupSide | undefined => (prefer && SIDES.includes(prefer) ? prefer : undefined)

/** Each side's unclamped origin, and the placement record for a chosen top-left. */
function geometry(anchor: PixelBox, popup: PixelSize, options: PlacementOptions) {
  const gap = options.gap ?? 10
  const arrowInset = options.arrowInset ?? 14
  const centreX = anchor.x + anchor.width / 2
  const centreY = anchor.y + anchor.height / 2
  const origin = (side: PopupSide): { x: number; y: number } => {
    switch (side) {
      case 'above': return { x: centreX - popup.width / 2, y: anchor.y - gap - popup.height }
      case 'below': return { x: centreX - popup.width / 2, y: anchor.y + anchor.height + gap }
      case 'right': return { x: anchor.x + anchor.width + gap, y: centreY - popup.height / 2 }
      case 'left': return { x: anchor.x - gap - popup.width, y: centreY - popup.height / 2 }
    }
  }
  const placed = (side: PopupSide, x: number, y: number, clipped: boolean): PopupPlacement => ({
    side, x, y, clipped,
    arrow: vertical(side) ? arrowOffset(centreX - x, popup.width, arrowInset) : arrowOffset(centreY - y, popup.height, arrowInset),
  })
  return { centreX, centreY, arrowInset, origin, placed }
}

export function placePopup(anchor: PixelBox, popup: PixelSize, container: PixelSize, options: PlacementOptions = {}): PopupPlacement {
  const margin = options.margin ?? 8
  const prefer = preferred(options.prefer)
  const order = prefer ? [prefer, ...SIDES.filter(side => side !== prefer)] : SIDES
  const { centreX, centreY, arrowInset, origin, placed } = geometry(anchor, popup, options)
  const maxX = container.width - margin - popup.width
  const maxY = container.height - margin - popup.height
  // A control keeps the same margin as the container edges.
  const avoid = (options.avoid ?? []).map(box => ({ x: box.x - margin, y: box.y - margin, width: box.width + 2 * margin, height: box.height + 2 * margin }))
  const blocker = (x: number, y: number): PixelBox | undefined => avoid.find(box => intersects({ x, y, width: popup.width, height: popup.height }, box))
  const fits = (x: number, y: number): boolean => x >= margin && x <= maxX && y >= margin && y <= maxY

  for (const side of order) {
    const { x: rawX, y: rawY } = origin(side)
    // Slide only along the anchor edge; the perpendicular coordinate keeps the gap.
    let x = vertical(side) ? clamp(rawX, margin, maxX) : rawX
    let y = vertical(side) ? rawY : clamp(rawY, margin, maxY)
    if (!fits(x, y)) continue
    const box = blocker(x, y)
    if (!box) return placed(side, x, y, false)
    // Slide once past the control in the way, while the stem can still reach the anchor.
    if (vertical(side)) {
      x = clamp(box.x + box.width / 2 > x + popup.width / 2 ? box.x - popup.width : box.x + box.width, margin, maxX)
      if (centreX - x < arrowInset || centreX - x > popup.width - arrowInset) continue
    } else {
      y = clamp(box.y + box.height / 2 > y + popup.height / 2 ? box.y - popup.height : box.y + box.height, margin, maxY)
      if (centreY - y < arrowInset || centreY - y > popup.height - arrowInset) continue
    }
    if (fits(x, y) && !blocker(x, y)) return placed(side, x, y, false)
  }

  // Nothing fits: take the side with the most room along its axis (ties keep the
  // standard order, and a side clear of the controls goes first) and clamp both
  // coordinates; the caller may scroll or shrink.
  const room: Record<PopupSide, number> = {
    above: anchor.y,
    below: container.height - (anchor.y + anchor.height),
    right: container.width - (anchor.x + anchor.width),
    left: anchor.x,
  }
  const ranked = [...SIDES].sort((a, b) => room[b] - room[a])
  const clamped = (side: PopupSide) => { const { x, y } = origin(side); return { side, x: clamp(x, margin, maxX), y: clamp(y, margin, maxY) } }
  const best = ranked.map(clamped).find(option => !blocker(option.x, option.y)) ?? clamped(ranked[0] ?? 'above')
  return placed(best.side, best.x, best.y, true)
}

/**
 * Placement for a popup that follows its anchor across pans and zooms. While the
 * anchor overlaps the container this is `placePopup`. Once it has left, the popup
 * keeps the offset of `last` (the latest placement made while it was inside), so
 * it moves rigidly with its marker and is never clamped back onto the map edge.
 * Without a `last` it takes the unclamped origin of `prefer` (default above).
 */
export function followPopup(anchor: PixelBox, container: PixelSize, last: PopupFollow | null, popup: PixelSize, options: PlacementOptions = {}): FollowedPlacement {
  const inside = anchor.x < container.width && anchor.x + anchor.width > 0 && anchor.y < container.height && anchor.y + anchor.height > 0
  let placement: PopupPlacement
  if (inside) placement = placePopup(anchor, popup, container, options)
  else if (last) placement = { ...last.placement, x: anchor.x + last.dx, y: anchor.y + last.dy }
  else {
    const side = preferred(options.prefer) ?? 'above'
    const { origin, placed } = geometry(anchor, popup, options)
    const { x, y } = origin(side)
    placement = placed(side, x, y, false)
  }
  return { placement, dx: placement.x - anchor.x, dy: placement.y - anchor.y, inside }
}
